/* DataImport — /admin/data-import
 * Step 1: Pick file
 * Step 2: Review & adjust column mappings (auto-mapped from DB field_definitions)
 * Step 3: Result summary
 */
import { useState, useRef, useEffect } from 'react';
import css from '../AdminPage.module.css';
import {
  useImportHistory,
  useImportPreview,
  useImportWithMappings,
  useBulkUpdateJobStatus,
  type ImportPreviewResult,
  type ImportSuggestedMapping,
  type ImportFieldOption,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import Badge from '../../../components/ui/Badge/Badge';
import EmptyState from '../../../components/ui/EmptyState/EmptyState';

type Mode = 'upload' | 'update';
type Step = 'pick' | 'map' | 'done';

interface ImportResult {
  success: number;
  errors: number;
  total: number;
  errorDetails?: string[];
  sampleMissingRefs?: string[];
  warnings?: string[];
  hasWarnings?: boolean;
}

export default function DataImport() {
  const { data: history = [], isLoading } = useImportHistory();
  const importPreview = useImportPreview();
  const importWithMaps = useImportWithMappings();
  const { showToast } = useToast();

  const [mode, setMode] = useState<Mode>('upload');
  const [step, setStep] = useState<Step>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  // mappings is the user-editable copy — one entry per CSV column (field='SKIP' means skip)
  const [mappings, setMappings] = useState<ImportSuggestedMapping[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [updateJobId, setUpdateJobId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const jobStatus = useBulkUpdateJobStatus(updateJobId);

  // When a bulk-update job completes, process the result and advance to the done step
  useEffect(() => {
    const job = jobStatus.data;
    if (!job) return;
    if (job.status === 'complete' && job.result) {
      const r = job.result as {
        successCount: number;
        failedCount: number;
        details: {
          errors?: { row: number; message: string }[];
          warnings?: { type: string; message: string; sample?: string[] }[];
        };
      };
      const success = r.successCount ?? 0;
      const errors = r.failedCount ?? 0;
      const sampleErrors = (r.details?.errors || [])
        .slice(0, 10)
        .map((e) => `Row ${e.row}: ${e.message}`);
      const warningMsgs = (r.details?.warnings || [])
        .filter((w) => w.type !== 'empty_row')
        .map((w) => w.message);
      const unmatchedW = (r.details?.warnings || []).find((w) => w.type === 'unmatched_caserefs');
      setResult({
        success,
        errors,
        total: success + errors,
        errorDetails: sampleErrors.length > 0 ? sampleErrors : undefined,
        sampleMissingRefs: unmatchedW?.sample,
        warnings: warningMsgs.length > 0 ? warningMsgs : undefined,
        hasWarnings: warningMsgs.length > 0,
      });
      setStep('done');
      setUpdateJobId(null);
      showToast(
        errors > 0
          ? `Update done with ${errors} error(s)`
          : `Update complete: ${success} rows processed`,
        errors > 0 ? 'warning' : 'success',
      );
    } else if (job.status === 'failed') {
      setResult({
        success: 0,
        errors: 1,
        total: 1,
        errorDetails: [job.error || 'Bulk update failed'],
      });
      setStep('done');
      setUpdateJobId(null);
      showToast(job.error || 'Bulk update failed', 'error');
    }
  }, [jobStatus.data?.status]);

  // ── helpers ─────────────────────────────────────────────────────────────────

  function acceptFile(f: File) {
    if (f.name.endsWith('.csv') || f.name.endsWith('.xlsx')) {
      setFile(f);
    } else {
      showToast('Only CSV or XLSX files are accepted', 'warning');
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) acceptFile(e.dataTransfer.files[0]);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) acceptFile(e.target.files[0]);
  }

  function handleClear() {
    setFile(null);
    setPreview(null);
    setMappings([]);
    setResult(null);
    setStep('pick');
    setUpdateJobId(null);
  }

  function handleModeChange(m: Mode) {
    setMode(m);
    handleClear();
  }

  function setMappingField(csvIndex: number, field: string) {
    setMappings((prev) => prev.map((m) => (m.csvIndex === csvIndex ? { ...m, field } : m)));
  }

  // ── step 1 → 2: preview ─────────────────────────────────────────────────────

  async function handlePreview() {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const data = await importPreview.mutateAsync(fd);
      setPreview(data);
      setMappings(data.suggestedMappings);
      setStep('map');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to preview file';
      showToast(msg, 'error');
    }
  }

  // ── step 2 → 3: upload with confirmed mappings ────────────────────────────

  async function handleConfirmUpload() {
    if (!file || !preview) return;

    // Validation: for new uploads, client_name must be mapped
    const norm = (f: string) => f.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (mode === 'upload') {
      const hasMapped = mappings.some((m) => norm(m.field) === 'clientname');
      if (!hasMapped) {
        showToast('You must map a column to "Client Name" before uploading', 'warning');
        return;
      }
    } else {
      const hasRef = mappings.some((m) => {
        const n = norm(m.field);
        return n === 'caseref' || n === 'contactid';
      });
      if (!hasRef) {
        showToast('You must map either CaseRef or ContactID for bulk updates', 'warning');
        return;
      }
    }

    try {
      const res = await importWithMaps.mutateAsync({ file, mappings, mode });
      const r = res as Record<string, unknown>;

      // Bulk update is now async — server returns a jobId for polling
      if (r.jobId) {
        setUpdateJobId(r.jobId as string);
        return;
      }

      // Upload mode: result returned synchronously as before
      const success = (r.successCount as number) ?? 0;
      const errors = (r.failedCount as number) ?? 0;
      const total = success + errors;
      const details = r.details as
        | {
            errors?: { row: number; message: string }[];
            warnings?: { type: string; message: string; sample?: string[] }[];
          }
        | undefined;
      const sampleErrors = (details?.errors || [])
        .slice(0, 10)
        .map((e) => `Row ${e.row}: ${e.message}`);
      const warningMsgs = (details?.warnings || [])
        .filter((w) => w.type !== 'empty_row')
        .map((w) => w.message);
      const unmatchedW = (details?.warnings || []).find((w) => w.type === 'unmatched_caserefs');
      setResult({
        success,
        errors,
        total,
        errorDetails: sampleErrors.length > 0 ? sampleErrors : undefined,
        sampleMissingRefs: unmatchedW?.sample,
        warnings: warningMsgs.length > 0 ? warningMsgs : undefined,
        hasWarnings: (r.hasWarnings as boolean) || false,
      });
      setStep('done');
      showToast(
        errors > 0
          ? `Import done with ${errors} error(s)`
          : `Import complete: ${success} rows processed`,
        errors > 0 ? 'warning' : 'success',
      );
    } catch (err: unknown) {
      // Axios wraps server JSON in err.response.data
      const axErr = err as { response?: { data?: Record<string, unknown> } };
      const data = axErr?.response?.data;
      const msg =
        (data?.message as string) ||
        (data?.error as string) ||
        (err instanceof Error ? err.message : 'Import failed');
      const sampleErrors = (data?.sampleErrors as string[]) || [];
      const sampleRefs = (data?.sampleMissingRefs as string[]) || [];
      const details = data?.details as { errors?: { row: number; message: string }[] } | undefined;
      const rowErrors =
        sampleErrors.length > 0
          ? sampleErrors
          : (details?.errors || [])
              .slice(0, 10)
              .map((e: { row: number; message: string }) => `Row ${e.row}: ${e.message}`);

      // failedCount might be missing (server thrown error) — treat as a failure
      const serverFailed = (data?.failedCount as number) ?? -1;
      setResult({
        success: (data?.successCount as number) ?? 0,
        errors: serverFailed < 0 ? 1 : serverFailed, // ensure non-zero so error panel shows
        total: Math.max(1, ((data?.successCount as number) ?? 0) + Math.max(0, serverFailed)),
        errorDetails: rowErrors.length > 0 ? rowErrors : [msg],
        sampleMissingRefs: sampleRefs.length > 0 ? sampleRefs : undefined,
      });
      setStep('done');
      showToast(msg, 'error');
    }
  }

  // ── confidence badge ─────────────────────────────────────────────────────────

  function ConfidenceBadge({ c }: { c: ImportSuggestedMapping['confidence'] }) {
    if (c === 'exact') return <Badge variant="success">auto-matched</Badge>;
    if (c === 'pattern') return <Badge variant="warning">pattern match</Badge>;
    return <Badge variant="neutral">unmatched</Badge>;
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  // ── STEP 1: File picker ───────────────────────────────────────────────────

  const stepPick = (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>📥 Data Import</h2>
      </div>

      <div className={css.tabs}>
        <button
          className={mode === 'upload' ? css.tabActive : css.tab}
          onClick={() => handleModeChange('upload')}
        >
          New Upload
        </button>
        <button
          className={mode === 'update' ? css.tabActive : css.tab}
          onClick={() => handleModeChange('update')}
        >
          Bulk Update
        </button>
      </div>

      <div
        className={`${css.dropZone} ${dragOver ? css.dropZoneActive : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {file ? (
          <div>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>📎 {file.name}</div>
            <div
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: 4 }}
            >
              {(file.size / 1024).toFixed(1)} KB — Click to change
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📁</div>
            <div>Drop a CSV or XLSX file here, or click to browse</div>
            <div style={{ fontSize: 'var(--font-size-xs)', marginTop: 4 }}>
              {mode === 'upload' ? 'Upload new case records' : 'Update existing records in bulk'}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          className={css.btnPrimary}
          onClick={handlePreview}
          disabled={!file || importPreview.isPending}
        >
          {importPreview.isPending ? 'Analysing…' : 'Next: Review Columns →'}
        </button>
        {file && (
          <button className={css.btnSecondary} onClick={handleClear}>
            Clear
          </button>
        )}
      </div>
    </div>
  );

  // ── STEP 2: Column mapping review ─────────────────────────────────────────

  const stepMap = preview && (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>🗂 Review Column Mapping</h2>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)', marginTop: 4 }}>
          {file?.name} — <strong>{preview.totalRows.toLocaleString()}</strong> rows detected. Review
          the auto-suggested mappings below, correct any mistakes and mark unwanted columns as{' '}
          <em>Skip</em>.
        </p>
      </div>

      <div style={{ overflowX: 'auto', marginTop: 16 }}>
        <table className={css.table} style={{ minWidth: 640 }}>
          <thead>
            <tr>
              <th style={{ width: '28%' }}>CSV Column</th>
              <th style={{ width: '22%' }}>Sample Value</th>
              <th style={{ width: '14%' }}>Auto-match</th>
              <th>Map to field</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => {
              const sample = preview.sampleRows[0]?.[m.csvIndex] ?? '—';
              return (
                <tr key={m.csvIndex}>
                  <td style={{ fontWeight: 500 }}>{m.csvColumnName}</td>
                  <td
                    style={{
                      color: 'var(--gray-400)',
                      fontSize: 'var(--font-size-xs)',
                      maxWidth: 180,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sample || <em style={{ opacity: 0.5 }}>empty</em>}
                  </td>
                  <td>
                    <ConfidenceBadge c={m.confidence} />
                  </td>
                  <td>
                    <select
                      className={css.formSelect}
                      value={m.field}
                      onChange={(e) => setMappingField(m.csvIndex, e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="SKIP">— Skip this column —</option>
                      {Object.entries(preview.fieldOptionsGrouped).map(([category, opts]) => (
                        <optgroup key={category} label={category}>
                          {opts.map((o: ImportFieldOption) => (
                            <option key={o.key} value={o.key}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary counts */}
      <div
        style={{
          marginTop: 12,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--gray-500)',
          display: 'flex',
          gap: 16,
        }}
      >
        <span>
          ✅ Mapped:{' '}
          <strong style={{ color: 'var(--success-600)' }}>
            {mappings.filter((m) => m.field !== 'SKIP').length}
          </strong>
        </span>
        <span>
          ⏭ Skipped: <strong>{mappings.filter((m) => m.field === 'SKIP').length}</strong>
        </span>
      </div>

      <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
        {updateJobId ? (
          // Progress view while async bulk-update job is running
          <div style={{ flex: 1 }}>
            <div className={css.progressBar} style={{ marginBottom: 8 }}>
              <div
                className={css.progressFill}
                style={{
                  width: `${jobStatus.data && jobStatus.data.total > 0 ? (jobStatus.data.completed / jobStatus.data.total) * 100 : 0}%`,
                }}
              />
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
              {jobStatus.data
                ? `${jobStatus.data.completed} / ${jobStatus.data.total} rows processed${jobStatus.data.failed > 0 ? ` (${jobStatus.data.failed} failed)` : ''}`
                : 'Starting update…'}
            </div>
          </div>
        ) : (
          <>
            <button className={css.btnSecondary} onClick={() => setStep('pick')}>
              ← Back
            </button>
            <button
              className={css.btnPrimary}
              onClick={handleConfirmUpload}
              disabled={importWithMaps.isPending}
            >
              {importWithMaps.isPending
                ? 'Uploading…'
                : `Confirm & ${mode === 'upload' ? 'Import Cases' : 'Apply Updates'}`}
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ── STEP 3: Result ────────────────────────────────────────────────────────

  const stepDone = result && (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>
          {result.errors === 0 && !result.errorDetails
            ? '✅ Import Complete'
            : result.success > 0
              ? '⚠️ Import Completed with Errors'
              : '❌ Import Failed'}
        </h2>
      </div>

      {/* Success banner */}
      {result.success > 0 && (
        <div
          style={{
            padding: 16,
            background: 'var(--success-50)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--success-300)',
            marginTop: 12,
          }}
        >
          <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
            {result.success} / {result.total} rows processed successfully
          </div>
        </div>
      )}

      {/* Error panel */}
      {(result.errors > 0 || (result.errorDetails && result.errorDetails.length > 0)) && (
        <div
          style={{
            padding: 16,
            background: 'var(--error-50, #fef2f2)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--error-300, #fca5a5)',
            marginTop: 12,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--error-700, #b91c1c)', marginBottom: 8 }}>
            {result.success === 0
              ? `All ${result.errors} rows failed`
              : `${result.errors} row(s) failed`}
          </div>
          {result.sampleMissingRefs && result.sampleMissingRefs.length > 0 && (
            <div style={{ marginBottom: 8, fontSize: 'var(--font-size-sm)' }}>
              <strong>CaseRefs not found in database:</strong>{' '}
              <code style={{ background: 'var(--gray-100)', padding: '2px 4px', borderRadius: 4 }}>
                {result.sampleMissingRefs.join(', ')}
              </code>
              {result.errors > result.sampleMissingRefs.length && (
                <span> (showing first {result.sampleMissingRefs.length})</span>
              )}
            </div>
          )}
          {result.errorDetails && result.errorDetails.length > 0 && (
            <details style={{ fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
              <summary style={{ fontWeight: 500, marginBottom: 4 }}>
                Show error details ({result.errorDetails.length} shown)
              </summary>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20, maxHeight: 200, overflowY: 'auto' }}>
                {result.errorDetails.map((e, i) => (
                  <li key={i} style={{ marginBottom: 2, color: 'var(--error-600, #dc2626)' }}>
                    {e}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Warnings panel */}
      {result.warnings && result.warnings.length > 0 && (
        <div
          style={{
            padding: 16,
            background: 'var(--warning-50, #fffbeb)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--warning-300, #fcd34d)',
            marginTop: 12,
          }}
        >
          <details style={{ fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
            <summary style={{ fontWeight: 500 }}>⚠️ {result.warnings.length} warning(s)</summary>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {result.warnings.map((w, i) => (
                <li key={i} style={{ marginBottom: 2, color: 'var(--warning-700, #a16207)' }}>
                  {w}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <button className={css.btnSecondary} onClick={handleClear}>
          Import Another File
        </button>
      </div>
    </div>
  );

  return (
    <>
      {step === 'pick' && stepPick}
      {step === 'map' && stepMap}
      {step === 'done' && stepDone}

      {/* Import History — always visible */}
      <div className={css.section}>
        <h3 className={css.sectionTitle} style={{ marginBottom: 16 }}>
          Import History
        </h3>
        {history.length === 0 ? (
          <EmptyState message="No import history" />
        ) : (
          <table className={css.table}>
            <thead>
              <tr>
                <th>File</th>
                <th>Operation</th>
                <th>Rows</th>
                <th>Success</th>
                <th>Errors</th>
                <th>Date</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td style={{ fontWeight: 500 }}>{h.filename}</td>
                  <td>
                    <Badge variant={h.operation === 'upload' ? 'info' : 'warning'}>
                      {h.operation}
                    </Badge>
                  </td>
                  <td>{h.total_rows}</td>
                  <td style={{ color: 'var(--success-600)' }}>{h.success_count}</td>
                  <td style={{ color: h.error_count > 0 ? 'var(--error-600)' : 'inherit' }}>
                    {h.error_count}
                  </td>
                  <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                    {new Date(h.created_at).toLocaleString()}
                  </td>
                  <td>{h.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
